import React, { useMemo } from "react";
import { Spinner } from "react-bootstrap";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useWindowSize } from "./../helper/hooks";
import PaginationForTableComponent from "./PaginationForTableComponent";
import PropTypes from "prop-types";

export const Table = (props) => {
  const {
    tableData,
    tableHeader,
    loading,
    handleSortTableByParams,
    topNavigation,
    defaultPageSize = 10,
    enablePagination = true,
    maxHeight,
  } = props;

  const screenSize = useWindowSize();
  const screenMobile = screenSize?.width <= 400 ? true : false;

  const data = useMemo(() => tableData ?? [], [tableData]);

  // Transform column definitions to TanStack format
  const columns = useMemo(
    () =>
      tableHeader?.map((column) => ({
        ...column,
        accessorKey: column.accessorKey || column.accessor,
        id: column.id || column.accessor,
        enableSorting: !column.disableSortBy,
        // Define default cell renderer that properly displays the value
        cell: column.cell || column.Cell || ((info) => info.getValue()),
        meta: column,
        enableHiding: true,
      })) ?? [],
    [tableHeader]
  );

  const previousPageSizeData = sessionStorage.getItem("previousPageSize");
  const initialPageSize = previousPageSizeData
    ? Number(previousPageSizeData)
    : defaultPageSize;

  // Initialize the table
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel() }),
    initialState: {
      ...(enablePagination && {
        pagination: {
          pageSize: initialPageSize,
        },
      }),
    },
    manualSorting: handleSortTableByParams ? true : false, // Use manual sorting only when handler is provided
    enableSorting: true,
  });

  // Extract pagination state from table (only if pagination is enabled)
  const paginationState = enablePagination ? table.getState().pagination : { pageIndex: 0, pageSize: data.length };
  const { pageIndex, pageSize } = paginationState;
  const pageCount = enablePagination ? table.getPageCount() : 1;
  const canPreviousPage = enablePagination ? table.getCanPreviousPage() : false;
  const canNextPage = enablePagination ? table.getCanNextPage() : false;


  // Create adapter functions to match the old API
  const paginationAdapter = {
    nextPage: () => enablePagination && table.nextPage(),
    previousPage: () => enablePagination && table.previousPage(),
    gotoPage: (page) => enablePagination && table.setPageIndex(page),
    setPageSize: (size) => enablePagination && table.setPageSize(size),
    pageOptions: enablePagination ? Array.from({ length: pageCount }, (_, i) => i) : [],
  };

  return (
    <>
      {enablePagination && (
        <PaginationForTableComponent
          showNavigation={topNavigation}
          screenMobile={screenMobile}
          pageSize={pageSize}
          pageIndex={pageIndex}
          canNextPage={canNextPage}
          tableData={tableData}
          setPageSize={paginationAdapter.setPageSize}
          canPreviousPage={canPreviousPage}
          previousPage={paginationAdapter.previousPage}
          gotoPage={paginationAdapter.gotoPage}
          pageCount={pageCount}
          nextPage={paginationAdapter.nextPage}
          pageOptions={paginationAdapter.pageOptions}
        />
      )}
      <div
        className="shared-table-sticky"
        style={{
          overflowX: "auto",
          ...(maxHeight && {
            maxHeight: maxHeight,
            overflowY: "auto",
          }),
        }}
      >
        <table className="shared-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={() => {
                      if (header.column.getCanSort()) {
                        if (handleSortTableByParams) {
                          handleSortTableByParams(header.column.id);
                        } else {
                          header.column.toggleSorting();
                        }
                      }
                    }}
                    style={{
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                    }}
                  >
                    <span>
                      <i
                        className="ti-exchange-vertical"
                        style={{
                          fontSize: "12px",
                          marginRight: "6px",
                          color: "#2c3e50",
                          display: !header.column.getCanSort() ? "none" : "inline-block"
                        }}
                      />
                    </span>
                    {header.column.columnDef.meta?.show === false 
                      ? null 
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {!loading && (
            <tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      const cellContent =
                        cell.column.columnDef.meta?.show === false
                          ? null
                          : flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            );

                      return <td key={cell.id}>{cellContent}</td>;
                    })}
                  </tr>
                );
              })}
              {table.getRowModel().rows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          )}
          {loading && (
            <tbody>
              <tr>
                <td
                  colSpan={tableHeader.length}
                  style={{ height: "100px", textAlign: "center" }}
                >
                  <Spinner variant="primary" animation="border" />
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      {enablePagination && (
        <PaginationForTableComponent
          showNavigation
          screenMobile={screenMobile}
          pageSize={pageSize}
          pageIndex={pageIndex}
          canNextPage={canNextPage}
          tableData={tableData}
          setPageSize={paginationAdapter.setPageSize}
          canPreviousPage={canPreviousPage}
          previousPage={paginationAdapter.previousPage}
          gotoPage={paginationAdapter.gotoPage}
          pageCount={pageCount}
          nextPage={paginationAdapter.nextPage}
          pageOptions={paginationAdapter.pageOptions}
        />
      )}
    </>
  );
};

Table.propTypes = {
  tableData: PropTypes.array,
  tableHeader: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      id: PropTypes.string,
      disableSortBy: PropTypes.bool,
      Cell: PropTypes.func,
      show: PropTypes.bool,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  handleSortTableByParams: PropTypes.func,
  topNavigation: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  enablePagination: PropTypes.bool,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
